import { Component, type ErrorInfo, type ReactNode } from 'react';
import styles from './ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  /** Optional label shown in the fallback UI (e.g. "Case File", "Admin") */
  section?: string;
  /** Optional custom fallback — rendered instead of the default UI */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[ErrorBoundary${this.props.section ? `: ${this.props.section}` : ''}]`,
      error,
      info.componentStack,
    );
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    const section = this.props.section ?? 'This section';

    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h2 className={styles.heading}>Something went wrong</h2>
          <p className={styles.message}>
            {section} encountered an unexpected error and couldn&apos;t render.
          </p>
          <button className={styles.retryBtn} onClick={this.handleRetry}>
            Try again
          </button>
        </div>
      </div>
    );
  }
}
